// Content-specific validation and formatting utilities
import * as acorn from 'acorn';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  formattedContent?: string;
}

export interface FormattingOptions {
  indentSize?: number;
  maxLineLength?: number;
}

// JSON validation and formatting
/** Replace $function(...) calls so JSON.parse can validate bodies that contain dynamic functions. */
export function stripFunctionCallsForJsonValidation(content: string): string {
  return content.replace(/\$\w+(?:\([^)]*\))?/g, '"__function__"');
}

export function validateJsonWithFunctions(content: string, options: FormattingOptions = {}): ValidationResult {
  if (!content.trim()) {
    return { isValid: true, formattedContent: '' };
  }
  try {
    JSON.parse(content);
    return validateAndFormatJSON(content, options);
  } catch {
    try {
      JSON.parse(stripFunctionCallsForJsonValidation(content));
      return { isValid: true, formattedContent: content };
    } catch (error) {
      return {
        isValid: false,
        error: `Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }
}

export function validateAndFormatJSON(content: string, options: FormattingOptions = {}): ValidationResult {
  if (!content.trim()) {
    return { isValid: true, formattedContent: '' };
  }

  try {
    const parsed = JSON.parse(content);
    const indentSize = options.indentSize || 2;
    const formatted = JSON.stringify(parsed, null, indentSize);
    
    return {
      isValid: true,
      formattedContent: formatted
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

// XML validation and formatting
export function validateAndFormatXML(content: string, options: FormattingOptions = {}): ValidationResult {
  if (!content.trim()) {
    return { isValid: true, formattedContent: '' };
  }

  try {
    // Basic XML validation using DOMParser
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(content, 'text/xml');
    
    // Check for parsing errors
    const parseError = xmlDoc.getElementsByTagName('parsererror');
    if (parseError.length > 0) {
      return {
        isValid: false,
        error: 'Invalid XML structure'
      };
    }

    // Simple formatting (basic indentation)
    const indentSize = options.indentSize || 2;
    const formatted = formatXMLString(content, indentSize);
    
    return {
      isValid: true,
      formattedContent: formatted
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Invalid XML: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

// HTML validation and formatting
export function validateAndFormatHTML(content: string, options: FormattingOptions = {}): ValidationResult {
  if (!content.trim()) {
    return { isValid: true, formattedContent: '' };
  }

  try {
    // text/html DOMParser is intentionally lenient and accepts nearly any text (no parsererror).
    // Require actual markup so Beautify rejects non-HTML bodies the same way XML does.
    if (!/<[a-zA-Z!/?][^>]*>/.test(content)) {
      return {
        isValid: false,
        error: 'Invalid HTML structure'
      };
    }

    // Basic HTML validation using DOMParser
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(content, 'text/html');
    
    // Check for parsing errors
    const parseError = htmlDoc.getElementsByTagName('parsererror');
    if (parseError.length > 0) {
      return {
        isValid: false,
        error: 'Invalid HTML structure'
      };
    }

    // Simple formatting (basic indentation)
    const indentSize = options.indentSize || 2;
    const formatted = formatHTMLString(content, indentSize);
    
    return {
      isValid: true,
      formattedContent: formatted
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Invalid HTML: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

// Advanced JavaScript validation and formatting
export function validateAndFormatJavaScript(content: string, options: FormattingOptions = {}): ValidationResult {
  if (!content.trim()) {
    return { isValid: true, formattedContent: '' };
  }

  try {
    // Use acorn for advanced JavaScript parsing
    acorn.parse(content, { 
      ecmaVersion: 2020, 
      sourceType: 'script',
      allowAwaitOutsideFunction: true,
      allowImportExportEverywhere: true
    });
    
    const indentSize = options.indentSize || 2;
    const formatted = formatJavaScriptString(content, indentSize);
    
    return {
      isValid: true,
      formattedContent: formatted
    };
  } catch (error) {
    return {
      isValid: false,
      error: `JavaScript syntax error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

// Text validation (minimal validation for plain text)
export function validateAndFormatText(content: string, options: FormattingOptions = {}): ValidationResult {
  // Plain text doesn't need validation, just return as-is
  return {
    isValid: true,
    formattedContent: content
  };
}

// Main validation function that routes to appropriate validator
export function validateAndFormatContent(
  content: string, 
  contentType: string, 
  options: FormattingOptions = {}
): ValidationResult {
  switch (contentType.toUpperCase()) {
    case 'JSON':
      return validateJsonWithFunctions(content, options);
    case 'XML':
      return validateAndFormatXML(content, options);
    case 'HTML':
      return validateAndFormatHTML(content, options);
    case 'JAVASCRIPT':
      return validateAndFormatJavaScript(content, options);
    case 'TEXT':
      return validateAndFormatText(content, options);
    default:
      return { isValid: true, formattedContent: content };
  }
}

// Helper functions for JavaScript validation (fallback for basic checks)
function checkUnmatchedBraces(content: string): boolean {
  const stack: string[] = [];
  for (const char of content) {
    if (char === '{') {
      stack.push(char);
    } else if (char === '}') {
      if (stack.length === 0) return true;
      stack.pop();
    }
  }
  return stack.length > 0;
}

function checkUnmatchedParens(content: string): boolean {
  const stack: string[] = [];
  for (const char of content) {
    if (char === '(') {
      stack.push(char);
    } else if (char === ')') {
      if (stack.length === 0) return true;
      stack.pop();
    }
  }
  return stack.length > 0;
}

function checkUnmatchedQuotes(content: string): boolean {
  let inSingleQuote = false;
  let inDoubleQuote = false;
  let inBacktick = false;
  
  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    const prevChar = i > 0 ? content[i - 1] : '';
    
    // Skip escaped quotes
    if (prevChar === '\\') continue;
    
    if (char === "'" && !inDoubleQuote && !inBacktick) {
      inSingleQuote = !inSingleQuote;
    } else if (char === '"' && !inSingleQuote && !inBacktick) {
      inDoubleQuote = !inDoubleQuote;
    } else if (char === '`' && !inSingleQuote && !inDoubleQuote) {
      inBacktick = !inBacktick;
    }
  }
  
  return inSingleQuote || inDoubleQuote || inBacktick;
}

/**
 * Lightweight JavaScript pretty-printer (brace/bracket/comma aware).
 * Preserves strings, template literals, and comments; adds indentation for objects/arrays.
 */
function formatJavaScriptString(js: string, indentSize: number): string {
  const indentStr = ' '.repeat(indentSize);
  let result = '';
  let indent = 0;
  let i = 0;
  let inSingle = false;
  let inDouble = false;
  let inTemplate = false;
  let inLineComment = false;
  let inBlockComment = false;
  let escape = false;
  let templateExprDepth = 0;

  const trimTrailingWs = () => {
    result = result.replace(/[ \t]+$/, '');
  };

  const emitNewline = () => {
    trimTrailingWs();
    if (!result.endsWith('\n')) {
      result += '\n';
    }
    result += indentStr.repeat(indent);
  };

  const skipSpaceTabsNewlines = () => {
    while (i < js.length && /[ \t\r\n]/.test(js[i])) {
      i += 1;
    }
  };

  while (i < js.length) {
    const c = js[i];
    const next = i + 1 < js.length ? js[i + 1] : '';

    if (inLineComment) {
      result += c;
      if (c === '\n') {
        inLineComment = false;
        result += indentStr.repeat(indent);
      }
      i += 1;
      continue;
    }

    if (inBlockComment) {
      result += c;
      if (c === '*' && next === '/') {
        result += '/';
        i += 2;
        inBlockComment = false;
        continue;
      }
      if (c === '\n') {
        result += indentStr.repeat(indent);
      }
      i += 1;
      continue;
    }

    if (inSingle || inDouble || (inTemplate && templateExprDepth === 0)) {
      result += c;
      if (escape) {
        escape = false;
      } else if (c === '\\') {
        escape = true;
      } else if (inSingle && c === "'") {
        inSingle = false;
      } else if (inDouble && c === '"') {
        inDouble = false;
      } else if (inTemplate && c === '`') {
        inTemplate = false;
      } else if (inTemplate && c === '$' && next === '{') {
        result += '{';
        i += 2;
        templateExprDepth = 1;
        continue;
      }
      i += 1;
      continue;
    }

    // Inside ${...} of a template literal — track brace depth, still format structure
    if (inTemplate && templateExprDepth > 0) {
      // fall through to structural formatting below, then adjust depth for { }
    }

    // Start comments
    if (c === '/' && next === '/') {
      result += '//';
      i += 2;
      inLineComment = true;
      continue;
    }
    if (c === '/' && next === '*') {
      result += '/*';
      i += 2;
      inBlockComment = true;
      continue;
    }

    // Start strings / templates
    if (c === "'") {
      inSingle = true;
      result += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inDouble = true;
      result += c;
      i += 1;
      continue;
    }
    if (c === '`') {
      inTemplate = true;
      templateExprDepth = 0;
      result += c;
      i += 1;
      continue;
    }

    if (c === '{' || c === '[') {
      result += c;
      indent += 1;
      i += 1;
      skipSpaceTabsNewlines();
      // Empty {} or []
      if (i < js.length && ((c === '{' && js[i] === '}') || (c === '[' && js[i] === ']'))) {
        indent = Math.max(0, indent - 1);
        result += js[i];
        i += 1;
        continue;
      }
      if (inTemplate && templateExprDepth > 0 && c === '{') {
        templateExprDepth += 1;
      }
      emitNewline();
      continue;
    }

    if (c === '}' || c === ']') {
      if (inTemplate && templateExprDepth > 0 && c === '}') {
        templateExprDepth -= 1;
        // Closing `${...}` itself — not a structural block brace
        if (templateExprDepth === 0) {
          result += '}';
          i += 1;
          continue;
        }
      }
      indent = Math.max(0, indent - 1);
      emitNewline();
      result += c;
      i += 1;
      continue;
    }

    if (c === ',') {
      result += c;
      i += 1;
      skipSpaceTabsNewlines();
      emitNewline();
      continue;
    }

    if (c === ':') {
      result += ': ';
      i += 1;
      while (i < js.length && /[ \t]/.test(js[i])) {
        i += 1;
      }
      continue;
    }

    if (c === ';') {
      result += c;
      i += 1;
      skipSpaceTabsNewlines();
      if (i < js.length) {
        emitNewline();
      }
      continue;
    }

    // Collapse whitespace outside strings/comments
    if (/[ \t\r\n]/.test(c)) {
      i += 1;
      skipSpaceTabsNewlines();
      if (result.length > 0 && !/\s$/.test(result) && i < js.length && !/[)}\],;]/.test(js[i])) {
        result += ' ';
      }
      continue;
    }

    result += c;
    i += 1;
  }

  return result.replace(/[ \t]+$/gm, '').trim();
}

// Simple XML formatting helper
function formatXMLString(xml: string, indentSize: number): string {
  // This is a basic implementation - for production, consider using a proper XML formatter
  let formatted = '';
  let indent = 0;
  const indentStr = ' '.repeat(indentSize);
  
  // Split by tags and process
  const parts = xml.split(/(<\/?[^>]+>)/);
  
  for (const part of parts) {
    if (part.trim() === '') continue;
    
    if (part.startsWith('</')) {
      // Closing tag - reduce indent
      indent = Math.max(0, indent - 1);
      formatted += indentStr.repeat(indent) + part + '\n';
    } else if (part.startsWith('<') && !part.startsWith('</')) {
      // Opening tag - add indent
      formatted += indentStr.repeat(indent) + part + '\n';
      if (!part.endsWith('/>')) {
        indent++;
      }
    } else {
      // Text content
      if (part.trim()) {
        formatted += indentStr.repeat(indent) + part.trim() + '\n';
      }
    }
  }
  
  return formatted.trim();
}

// Simple HTML formatting helper
function formatHTMLString(html: string, indentSize: number): string {
  // This is a basic implementation - for production, consider using a proper HTML formatter
  let formatted = '';
  let indent = 0;
  const indentStr = ' '.repeat(indentSize);
  
  // Split by tags and process
  const parts = html.split(/(<\/?[^>]+>)/);
  
  for (const part of parts) {
    if (part.trim() === '') continue;
    
    if (part.startsWith('</')) {
      // Closing tag - reduce indent
      indent = Math.max(0, indent - 1);
      formatted += indentStr.repeat(indent) + part + '\n';
    } else if (part.startsWith('<') && !part.startsWith('</')) {
      // Opening tag - add indent
      formatted += indentStr.repeat(indent) + part + '\n';
      if (!part.endsWith('/>') && !part.includes('<!')) {
        indent++;
      }
    } else {
      // Text content
      if (part.trim()) {
        formatted += indentStr.repeat(indent) + part.trim() + '\n';
      }
    }
  }
  
  return formatted.trim();
}

// Get syntax highlighting language for different content types
export function getSyntaxHighlightingLanguage(contentType: string): string {
  switch (contentType.toUpperCase()) {
    case 'JSON':
      return 'json';
    case 'XML':
      return 'xml';
    case 'HTML':
      return 'html';
    case 'JAVASCRIPT':
      return 'javascript';
    case 'TEXT':
      return 'text';
    default:
      return 'text';
  }
}

// Get content type description for UI
export function getContentTypeDescription(contentType: string): string {
  switch (contentType.toUpperCase()) {
    case 'JSON':
      return 'JavaScript Object Notation - Data interchange format';
    case 'XML':
      return 'Extensible Markup Language - Document format';
    case 'HTML':
      return 'HyperText Markup Language - Web page structure';
    case 'JAVASCRIPT':
      return 'JavaScript code - Client-side scripting';
    case 'TEXT':
      return 'Plain text - Unformatted text content';
    default:
      return 'Unknown content type';
  }
} 